import { Alert, Box, Typography } from "@mui/material";
import React from "react";
import LogoLoader from "../common/LogoLoader";
import type { EntityDetailMode } from "../../types/entityRegistry";

interface EntityDetailStatusProps {
  loading: boolean;
  // An error or not-found message; shown as a warning
  error: string | null;
  mode?: EntityDetailMode;
}

/**
 * The loading and error states of an entity detail, in one place.
 * A Detail component renders this while it has no entity to show;
 * a not-found state is an error message like any other.
 */
export const EntityDetailStatus: React.FC<EntityDetailStatusProps> = ({
  loading,
  error,
  mode = "overlay",
}) => {
  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: mode === "standalone" ? "400px" : "100%",
        }}
      >
        <LogoLoader size="panel" surface="paper" label="Loading the details" />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  return null;
};

interface EntityDetailComingSoonProps {
  // The singular type name, e.g. "product"
  entityType: string;
}

/**
 * The fallback for a type that has no detail view yet
 */
export const EntityDetailComingSoon: React.FC<EntityDetailComingSoonProps> = ({
  entityType,
}) => (
  <Box sx={{ p: 2 }}>
    <Typography variant="h6" gutterBottom>
      {entityType.charAt(0).toUpperCase() + entityType.slice(1)} Details
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Detailed view for {entityType}s is coming soon.
    </Typography>
  </Box>
);
